﻿import type {
  ColumnEvent,
  ColumnGroupOpenedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModifiedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import type {
  AgEventType,
  ColumnGroupPreferenceState,
  ColumnPreferenceState,
} from 'components/shared/Blotter/Grid/blotter.model';
import { hasDifferencesIgnoringUndefined } from 'components/shared/Blotter/Grid/Hooks/utils';
import { isEqual } from 'lodash-es';
import { useCallback } from 'react';

type AllColumnEvents<Item> =
  | ColumnEvent<any, Item>
  | ColumnMovedEvent<Item>
  | ColumnResizedEvent<Item>
  | ColumnPinnedEvent<Item>
  | ColumnVisibleEvent<Item>
  | SortChangedEvent<Item>
  | ColumnGroupOpenedEvent<Item>
  | FilterChangedEvent<Item>
  | FilterModifiedEvent<Item>;

function isColumnEvent(event: any): event is ColumnEvent {
  return (event as ColumnEvent).source !== undefined;
}

function getColumnEventSource(event: any) {
  if (isColumnEvent(event)) {
    return event.source;
  }
  return undefined;
}

export function useOnServerSideOnColumnChanged<Item = any>(
  currentColumns: readonly ColumnPreferenceState[] | null,
  currentColumnGroups: readonly ColumnGroupPreferenceState[] | undefined,
  onBlotterColumnStateChanged: (
    columnState: readonly ColumnPreferenceState[],
    newColumnGroups?: readonly ColumnGroupPreferenceState[],
  ) => void,
) {
  return useCallback(
    ({ api, type, ...event }: AllColumnEvents<Item>) => {
      // Wait event is finished before applying it e.g: we want to persist columns positions when the column are finished to be moved
      if ('finished' in event && !event.finished) {
        return;
      }
      if ('filterInstance' in event && 'context' in event && event.context === undefined) {
        return;
      }
      const eventSource = getColumnEventSource(event);
      if (eventSource === 'rowDataUpdated' || eventSource === 'api') {
        // Ignore all events raised from the rows updated by restoring previous columns state, we don't care about columns changed in this case
        return;
      }

      const globalModel = api?.getFilterModel();
      const newColumnState = api.getAllGridColumns().map(col => {
        const colId = col.getColId();
        const filterModel = globalModel?.[colId];
        return {
          colId,
          width: col.getActualWidth(),
          pinned: col.getPinned(),
          hide: !col.isVisible(),
          sort: col.getSort(),
          filterModel: filterModel,
        } as ColumnPreferenceState;
      });

      const newColumnGroupState = api.getColumnGroupState().map(({ groupId, open }) => ({ groupId, isExpanded: open }));

      const eventType = type as AgEventType;
      if (eventType === 'columnMoved') {
        const currentColIds = currentColumns?.map(c => c.colId) || [];
        const newColdIds = newColumnState.map(c => c.colId);

        if (!isEqual(currentColIds, newColdIds)) {
          onBlotterColumnStateChanged(newColumnState, newColumnGroupState);
          return;
        }
        return;
      }

      if (
        hasDifferencesIgnoringUndefined(currentColumns ?? [], newColumnState) ||
        hasDifferencesIgnoringUndefined(currentColumnGroups ?? [], newColumnGroupState)
      ) {
        onBlotterColumnStateChanged(newColumnState, newColumnGroupState);
      }
    },
    [currentColumns, onBlotterColumnStateChanged, currentColumnGroups],
  );
}
